<template>
  <section
    :id="`qc-finding-${finding.id}`"
    class="qc-finding mb-2 rounded-lg border border-l-4 border-gray-200 shadow-xs"
    :class="[edgeClass, isResolved ? 'bg-gray-50 opacity-75' : 'bg-white']"
    :data-severity="finding.type === 'vaba' ? 'none' : severity.key"
    :data-basis="finding.basisKind ?? 'none'"
    :data-resolution="finding.resolution ?? ''"
    data-testid="qc-finding"
  >
    <!--
      White card, hairline border, a 4 px coloured LEFT EDGE for severity —
      colour is an edge, an icon or a number, never a fill (design-spec §17.1).
      Collapsed by default; a resolved finding collapses and its header carries
      the answer instead of the basis chip (§16.6).
    -->
    <!--
      RED-373: `Võta tagasi` sits in the collapsed header next to the answer, so
      an answer can be undone without opening the card. A SIBLING of the header
      button, never inside it — a button inside a button is invalid HTML and
      its click would toggle the card as well.
    -->
    <div class="flex items-center">
      <button
        type="button"
        class="flex min-w-0 flex-1 items-center gap-2.5 rounded-r-lg p-3 text-left transition-colors hover:bg-gray-100"
        :aria-expanded="open"
        data-testid="qc-finding-header"
        @click="emit('toggle', finding.id)"
      >
        <span
          v-tooltip.top="severityTooltip"
          class="pi shrink-0 text-base"
          :class="[severity.icon, severity.colour]"
          aria-hidden="true"
        ></span>
        <span
          class="min-w-0 flex-1 leading-snug"
          :class="isResolved ? 'font-normal text-gray-500 line-through' : 'font-medium text-gray-800'"
          data-testid="qc-finding-title"
        >{{ finding.title }}</span>
        <span
          v-if="isResolved"
          class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-semibold"
          :class="finding.resolution === 'fixed' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-gray-100 text-gray-700'"
          data-testid="qc-finding-resolution"
        >
          <span
            class="pi text-[0.625rem]"
            :class="finding.resolution === 'fixed' ? 'pi-check' : 'pi-times'"
            aria-hidden="true"
          ></span>
          {{ finding.resolution === 'fixed'
            ? $t('messages.evaluation.qualityCheck.finding.resolvedFixed')
            : $t('messages.evaluation.qualityCheck.finding.resolvedNotAnError') }}
        </span>
        <QcBasisChip v-else-if="finding.basisKind" :kind="finding.basisKind" />
        <span
          class="pi pi-chevron-right shrink-0 text-[0.7rem] text-gray-500 transition-transform"
          :class="{ 'rotate-90': open }"
          aria-hidden="true"
        ></span>
      </button>
      <Button
        v-if="isResolved && !open && !readOnly"
        :label="$t('messages.evaluation.qualityCheck.finding.undo')"
        severity="secondary"
        text
        size="small"
        class="mr-2 w-auto! shrink-0"
        :loading="busy"
        data-testid="qc-finding-undo-header"
        @click="emit('resolve', finding.id, null)"
      />
    </div>

    <div v-if="open" class="border-t border-gray-100 px-3 pb-3" data-testid="qc-finding-body">
      <!--
        A standard citation on checklist items 12-22 is a FABRICATION, not a
        stronger finding: those items are approver practice with no standard
        behind them (§17.2, AC 27). The chip's rank says it; this line says it
        in words so nobody has to decode a chip.
      -->
      <QcNotice
        v-if="fabricationRisk"
        tone="warn"
        icon="pi-exclamation-triangle"
        class="mt-3"
        data-testid="qc-finding-fabrication-warning"
      >
        {{ $t('messages.evaluation.qualityCheck.finding.fabricationRisk') }}
      </QcNotice>

      <p v-if="finding.problem" class="whitespace-pre-line pt-3 text-sm text-gray-700" data-testid="qc-finding-problem">
        {{ finding.problem }}
      </p>

      <!--
        The place in the report: a CHAPTER and the quoted line with the lines
        either side greyed. No page number, ever — a .docx carries none, Word
        paginates at render time (§16.4). Once the file is deleted the place is
        gone with it, and the card says so instead of quoting it.
      -->
      <div class="pt-3">
        <p class="qc-eyebrow">{{ $t('messages.evaluation.qualityCheck.finding.place') }}</p>
        <div class="overflow-hidden rounded-lg border border-gray-200 bg-white" data-testid="qc-finding-excerpt">
          <p v-if="fileDeleted" class="px-3 py-2 text-sm text-gray-400" data-testid="qc-finding-place-deleted">
            {{ chapterLine
              ? $t('messages.evaluation.qualityCheck.deleted.placeChapter', { chapter: chapterLine })
              : $t('messages.evaluation.qualityCheck.deleted.place') }}
          </p>
          <template v-else-if="excerpt">
            <p
              v-if="chapterLine"
              class="flex items-center gap-2 border-b border-gray-200 bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-500"
              data-testid="qc-finding-chapter"
            >
              <span class="pi pi-file" aria-hidden="true"></span>{{ chapterLine }}
            </p>
            <div class="break-words px-3 py-2 font-mono text-xs leading-7">
              <span v-for="(line, i) in excerpt.before" :key="`b-${i}`" class="block text-gray-400">{{ line }}</span>
              <span
                class="rounded-xs bg-yellow-400/10 px-0.5 font-semibold text-gray-800 ring-1 ring-yellow-400/20"
                data-testid="qc-finding-excerpt-line"
              >{{ excerpt.line }}</span>
              <span v-for="(line, i) in excerpt.after" :key="`a-${i}`" class="block text-gray-400">{{ line }}</span>
            </div>
          </template>
          <!-- RED-372: a chapter without a quoted line is still a place. -->
          <p
            v-else-if="chapterLine"
            class="flex items-center gap-2 bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-500"
            data-testid="qc-finding-chapter"
          >
            <span class="pi pi-file" aria-hidden="true"></span>{{ chapterLine }}
          </p>
          <p v-else class="px-3 py-2 text-sm text-gray-400" data-testid="qc-finding-no-place">
            {{ $t('messages.evaluation.qualityCheck.finding.noPlace') }}
          </p>
        </div>
      </div>

      <div v-if="finding.fixSuggestion" class="pt-3">
        <p class="qc-eyebrow">{{ $t('messages.evaluation.qualityCheck.finding.fix') }}</p>
        <p
          class="whitespace-pre-line rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700"
          data-testid="qc-finding-fix"
        >{{ finding.fixSuggestion }}</p>
      </div>

      <div class="pt-3">
        <p class="qc-eyebrow">{{ $t('messages.evaluation.qualityCheck.finding.basis') }}</p>
        <!--
          RED-373: the raw `[standard]` / `[kinnitaja praktika]` markers are the
          chip's job, not the reader's. Each checkpoint number is its own span
          with the item's full text on hover.
        -->
        <p class="text-sm text-gray-500" data-testid="qc-finding-checklist">
          {{ basisLine || $t('messages.evaluation.qualityCheck.finding.noBasis') }}
          <span class="text-gray-400">·
            <template v-if="checkpointNumbers.length === 0">{{ $t('messages.evaluation.qualityCheck.finding.noCheckpoint') }}</template>
            <template v-else>
              {{ $t('messages.evaluation.qualityCheck.finding.checkpointPrefix', checkpointNumbers.length) }}
              <template v-for="(no, i) in checkpointNumbers" :key="`cp-${no}`"><template v-if="i > 0">, </template><span
                v-tooltip.top="checklistText[no] || null"
                class="cursor-help underline decoration-dotted underline-offset-2"
                :data-checkpoint="no"
                data-testid="qc-finding-checkpoint"
              >{{ no }}</span></template><template v-if="checkpointLabel">: {{ checkpointLabel }}</template>
            </template>
          </span>
        </p>
      </div>

      <!-- RED-342 D7: the EVS 875 passages the machine looked up for this
           finding's QUESTION. A PDF source has pages; a text source has none,
           and the line says so rather than implying equal precision. -->
      <div v-if="sources.length" class="pt-3" data-testid="qc-finding-sources">
        <p class="qc-eyebrow">{{ $t('messages.evaluation.qualityCheck.finding.sources') }}</p>
        <ul class="text-sm text-gray-600">
          <li v-for="source in sources" :key="source.title" data-testid="qc-finding-source">
            {{ source.pages.length
              ? $t('messages.evaluation.qualityCheck.finding.sourcePage', { title: source.title, pages: source.pages.join(', ') })
              : $t('messages.evaluation.qualityCheck.finding.sourceNoPage', { title: source.title }) }}
          </li>
        </ul>
        <p class="mt-1 text-xs text-gray-400">{{ $t('messages.evaluation.qualityCheck.finding.sourcesNote') }}</p>
      </div>

      <!--
        One action: `Märgi parandatuks`, the appraiser's own note that he fixed
        the point in his Word file. Undoable and persisted SERVER-SIDE
        (design-spec §5) — never localStorage: a visual-only check passes while
        nothing is saved, which is exactly the bug RED-235 shipped. Pressing it
        again clears it; so does `Võta tagasi`.
        `Ei ole viga` was removed from the card (RED-388 follow-up, 2026-10-15):
        it confused appraisers and never reached the admin feedback list, which
        is what `Kas see leid oli õige?` below is for. The server still accepts
        and stores `not_an_error`, so an old row keeps its header badge and
        its `Võta tagasi`.
        `Märgi parandatuks` is solid green — a deliberate, recorded exception to
        "the Kiirhindamine pill is the only green CTA" (§17.5).
        Not for a `vaba` row: prose to read, not a finding to resolve (§4) —
        the server refuses a resolution on it too. Not in the REdata admin's
        read-only view either (RED-389): the header badge shows the answer.
      -->
      <div
        v-if="finding.type !== 'vaba' && !readOnly"
        class="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3"
      >
        <Button
          :label="$t('messages.evaluation.qualityCheck.finding.markFixed')"
          icon="pi pi-check"
          size="small"
          class="qc-fixed-button w-auto!"
          :class="{ 'qc-fixed-pressed': finding.resolution === 'fixed' }"
          :aria-pressed="finding.resolution === 'fixed'"
          :loading="busy"
          data-testid="qc-finding-mark-fixed"
          @click="toggle('fixed')"
        />
        <span class="flex-1"></span>
        <Button
          v-if="isResolved"
          :label="$t('messages.evaluation.qualityCheck.finding.undo')"
          severity="secondary"
          text
          size="small"
          class="w-auto!"
          :loading="busy"
          data-testid="qc-finding-undo"
          @click="emit('resolve', finding.id, null)"
        />
      </div>

      <!--
        RED-388, Variant B (design-spec §6): a SEPARATE row, independent of the
        resolution button above. `Märgi parandatuks` says what the appraiser
        did with the report; this says whether the machine was right. A verdict
        click saves at once; the note goes with `Saada`; clicking the chosen
        verdict again takes the answer back. Stored server-side, never
        localStorage (the RED-235 lesson).
      -->
      <div
        v-if="finding.type !== 'vaba' && sendFeedback"
        class="mt-3 border-t border-gray-100 pt-3"
        data-testid="qc-feedback"
        :data-verdict="feedback?.verdict ?? ''"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm font-medium text-gray-700">{{ $t('messages.evaluation.qualityCheck.feedback.question') }}</span>
          <Button
            v-for="option in VERDICTS"
            :key="option"
            v-tooltip.top="feedback?.verdict === option ? $t('messages.evaluation.qualityCheck.feedback.clearHint') : null"
            :label="$t(`messages.evaluation.qualityCheck.feedback.${option}`)"
            severity="secondary"
            outlined
            size="small"
            class="w-auto!"
            :class="{ 'qc-pressed': feedback?.verdict === option }"
            :aria-pressed="feedback?.verdict === option"
            :disabled="feedbackBusy"
            :data-testid="`qc-feedback-${option}`"
            @click="chooseVerdict(option)"
          />
        </div>

        <div v-if="feedback" class="mt-3">
          <label
            :for="`qc-feedback-note-${finding.id}`"
            class="mb-1 block text-sm text-gray-700"
          >{{ $t('messages.evaluation.qualityCheck.feedback.noteLabel') }}</label>
          <Textarea
            :id="`qc-feedback-note-${finding.id}`"
            v-model="noteDraft"
            rows="2"
            auto-resize
            fluid
            :maxlength="NOTE_MAX"
            :invalid="noteError !== ''"
            :placeholder="$t('messages.evaluation.qualityCheck.feedback.notePlaceholder')"
            data-testid="qc-feedback-note"
            @input="onNoteInput"
          />
          <p
            v-if="noteError"
            class="mt-1 text-sm text-red-600"
            role="alert"
            data-testid="qc-feedback-note-error"
          >{{ noteError }}</p>
          <p class="mt-1 text-xs text-gray-500">{{ $t('messages.evaluation.qualityCheck.feedback.noteHint') }}</p>
          <div class="mt-2 flex flex-wrap items-center gap-3">
            <Button
              :label="$t('messages.evaluation.qualityCheck.feedback.send')"
              size="small"
              class="w-auto!"
              :loading="feedbackBusy"
              :disabled="!noteChanged"
              data-testid="qc-feedback-send"
              @click="sendNote"
            />
            <span class="text-sm text-gray-500" data-testid="qc-feedback-status">{{ statusLine }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import QcBasisChip from './QcBasisChip.vue'
import QcNotice from './QcNotice.vue'

/**
 * RED-351 — one finding (prototype v3 `findingHtml`).
 *
 * This component carries the single design decision the whole ticket exists
 * for: a **standards** finding (cites EVS 875, the reader can go and look it
 * up) and a **practice** finding (cites our checklist, verifiable against
 * nothing) must be distinguishable at a glance. Identical rendering produces
 * identical trust, and that is wrong in BOTH directions. Severity owns the
 * colour; basis is a grey chip (§16.5).
 *
 * Open/closed is owned by the parent (`QcFindingsScreen`): the index, the
 * checklist and a resolution all open or close cards, so one place decides.
 */
const props = defineProps({
  finding: { type: Object, required: true },
  open: { type: Boolean, default: false },
  /**
   * Owned by the PARENT, the only thing that knows when the PATCH finished —
   * including when it FAILED. A flag cleared by watching the resolution would
   * stay stuck forever on a failure, because then the value never changes.
   */
  busy: { type: Boolean, default: false },
  /** The .docx is gone (retention or `Eemalda fail`): no place to quote. */
  fileDeleted: { type: Boolean, default: false },
  /** RED-373: checkpoint number → its full text, for the hover on each number. */
  checklistText: { type: Object, default: () => ({}) },
  /**
   * RED-388: `useReportCheck().sendFeedback` / `clearFeedback`. Both resolve
   * to `{ ok, noteError }` and never throw, so the busy flag always clears.
   * Without them the feedback row is not drawn.
   */
  sendFeedback: { type: Function, default: null },
  clearFeedback: { type: Function, default: null },
  /**
   * RED-389: the REdata admin reading another appraiser's run. No resolution
   * button and no undo; the resolution badge stays, so the admin sees what
   * the appraiser marked. The feedback row is already absent without
   * `sendFeedback`.
   */
  readOnly: { type: Boolean, default: false },
})

const emit = defineEmits(['toggle', 'resolve'])

const { t } = useI18n()

const isResolved = computed(() => props.finding.resolution !== null && props.finding.resolution !== undefined)

// Severity owns the colour: red / amber / grey (the design system's
// --red-600 / --severity-warn / --gray-500). Indigo stays reserved for the map.
const SEVERITY = {
  blokeeriv: { icon: 'pi-ban', colour: 'text-red-600', edge: 'border-l-red-600', slug: 'blokeeriv' },
  'vajab parandust': { icon: 'pi-exclamation-triangle', colour: 'text-amber-500', edge: 'border-l-amber-500', slug: 'vajabParandust' },
  soovituslik: { icon: 'pi-lightbulb', colour: 'text-gray-500', edge: 'border-l-gray-500', slug: 'soovituslik' },
}

// A `vaba` row carries no severity marker at all (design-spec §4).
const NO_SEVERITY = { icon: 'pi-lightbulb', colour: 'text-gray-400', edge: 'border-l-gray-400', slug: null }

// A `leid` the machine gave no severity reads as `soovituslik`, the same as
// the screen's tiles, filter and sort (RED-373). Only `vaba` prose has none.
const severity = computed(() => {
  if (props.finding.type === 'vaba') {
    return NO_SEVERITY
  }
  const key = SEVERITY[props.finding.severity] ? props.finding.severity : 'soovituslik'
  return { ...SEVERITY[key], key }
})
const edgeClass = computed(() => severity.value.edge)

const severityTooltip = computed(() => {
  const slug = severity.value.slug
  if (!slug) {
    return t('messages.evaluation.qualityCheck.results.vabaSub')
  }
  return `${t(`messages.evaluation.qualityCheck.severity.${slug}`)}. ${t(`messages.evaluation.qualityCheck.severityHint.${slug}`)}`
})

const checklistItems = computed(() => props.finding.checklistItems ?? [])

/** `[{ title, pages }]` from the server, only on standard-based findings (RED-342). */
const sources = computed(() => (props.finding.sources ?? []).filter(source => source?.title))

// "· kontrollpunkt 15: <nimi>" — a raw question id never reaches the screen
// (§10.2); the server does not even send one.
const checkpointNumbers = computed(() => checklistItems.value.map(item => String(item).trim()).filter(Boolean))

/**
 * `checkLabel` names the PRIMARY item only, so it is shown only when the
 * finding touches one item — under "kontrollpunktid 2, 12" it would mislabel
 * the rest (RED-373). The hover on each number carries the full text instead.
 */
const checkpointLabel = computed(() => (checkpointNumbers.value.length === 1 ? props.finding.checkLabel ?? '' : ''))

/** The basis markers are shown by the chip; strip them from the reader's text. */
const basisLine = computed(() =>
  (props.finding.basisText ?? '')
    .replace(/\[(?:standard|kinnitaja praktika)\]\s*/giu, '')
    // "[kinnitaja praktika], punkt 18" must not become ", punkt 18".
    .replace(/^[\s,;:.–-]+/u, '')
    .replace(/\s*([,;])\s*[,;]/gu, '$1')
    .replace(/\s{2,}/gu, ' ')
    .trim(),
)

/**
 * Items 12-22 have NO standard behind them — a standard citation there is
 * invented. The server decides (`ReportCheckFinding::fabricationRisk()`,
 * RED-376): only when EVERY tagged item is 12-22 and the basis claims a
 * standard, so a 7+12+22 finding is not flagged.
 */
const fabricationRisk = computed(() => props.finding.fabricationRisk === true)

/**
 * `location` is a JSON column `{ chapter, chapter_title }`; a plain string is
 * tolerated so an older row still renders.
 */
const chapterLine = computed(() => {
  const raw = props.finding.location
  if (!raw) {
    return ''
  }
  if (typeof raw === 'string') {
    return raw
  }
  return [raw.chapter, raw.chapter_title].filter(Boolean).join(' · ')
})

/** `excerpt` is JSON too; `before` / `after` are arrays of lines. */
const excerpt = computed(() => {
  const raw = props.finding.excerpt
  if (!raw) {
    return null
  }
  if (typeof raw === 'string') {
    return { before: [], line: raw, after: [] }
  }
  const lines = value => {
    if (!value) {
      return []
    }
    return Array.isArray(value) ? value.filter(Boolean) : [value]
  }
  return { before: lines(raw.before), line: raw.line ?? '', after: lines(raw.after) }
})

// ---------------------------------------------------------------- RED-388

const VERDICTS = ['oige', 'vale']
/** Mirrors `ReportCheckFindingFeedback::NOTE_MAX`; the server is the truth. */
const NOTE_MAX = 500

/** The viewer's own saved verdict `{ verdict, note, updatedAt }`, or null. */
const feedback = computed(() => props.finding.feedback ?? null)

const noteDraft = ref(feedback.value?.note ?? '')
const noteError = ref('')
const feedbackBusy = ref(false)
/** What this page did last, so the line under `Saada` can say so. */
const lastAction = ref(null)

const noteChanged = computed(() => noteDraft.value.trim() !== (feedback.value?.note ?? ''))

// A reload brings the saved note; take it unless the appraiser is typing.
watch(
  () => feedback.value?.note ?? '',
  saved => {
    if (!noteChanged.value || lastAction.value === 'sent') {
      noteDraft.value = saved
    }
  },
)

const statusLine = computed(() => {
  if (lastAction.value === 'sent') {
    return t('messages.evaluation.qualityCheck.feedback.sent')
  }
  if (lastAction.value === 'verdict') {
    return t('messages.evaluation.qualityCheck.feedback.verdictSaved')
  }
  const at = feedback.value?.updatedAt ? new Date(feedback.value.updatedAt) : null
  if (!at || Number.isNaN(at.getTime())) {
    return ''
  }
  return t('messages.evaluation.qualityCheck.feedback.saved', {
    date: at.toLocaleDateString('et-EE'),
    time: at.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' }),
  })
})

const withBusy = async action => {
  if (feedbackBusy.value) {
    return null
  }
  feedbackBusy.value = true
  try {
    return await action()
  } finally {
    feedbackBusy.value = false
  }
}

/** Typing again after `Saada`: the "Saadetud" line no longer describes the field. */
const onNoteInput = () => {
  noteError.value = ''
  if (lastAction.value === 'sent') {
    lastAction.value = null
  }
}

/** The chosen verdict again = take it back (note included). */
const chooseVerdict = verdict =>
  withBusy(async () => {
    noteError.value = ''
    if (feedback.value?.verdict === verdict) {
      const result = await props.clearFeedback(props.finding.id)
      if (result?.ok) {
        lastAction.value = null
        noteDraft.value = ''
      }
      return
    }
    const result = await props.sendFeedback(props.finding.id, verdict)
    if (result?.ok) {
      lastAction.value = feedback.value?.note ? null : 'verdict'
    }
  })

const sendNote = () =>
  withBusy(async () => {
    const result = await props.sendFeedback(props.finding.id, feedback.value.verdict, noteDraft.value.trim())
    if (result?.ok) {
      lastAction.value = 'sent'
      noteError.value = ''
    } else if (result?.noteError) {
      noteError.value = result.noteError
    }
  })

/** Pressing the active resolution again clears it (prototype v3). */
const toggle = resolution => {
  if (props.busy) {
    return
  }
  emit('resolve', props.finding.id, props.finding.resolution === resolution ? null : resolution)
}
</script>

<style scoped>
@reference '../../../../css/tailwind.css';

.qc-eyebrow {
  @apply mb-1 text-xs font-medium uppercase tracking-wider text-gray-400;
}

/* §17.5: the one deliberate green CTA outside the chrome. */
.qc-fixed-button {
  @apply border-emerald-600 bg-emerald-600 text-white;
}

.qc-fixed-button:hover {
  @apply border-teal-600 bg-teal-600;
}

/* RED-373: `Märgi parandatuks` while it is the finding's answer — darker, ringed. */
.qc-fixed-pressed,
.qc-fixed-pressed:hover {
  @apply border-emerald-800 bg-emerald-800 ring-2 ring-emerald-300 ring-offset-1;
}

/* `Õige` / `Vale` while it is the appraiser's verdict. */
.qc-pressed {
  @apply border-blue-500 bg-blue-500 text-white;
}
</style>
